package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.EventServerEventSearchData
import com.neurobs.ccab.db.EventServerEventData
import com.neurobs.errors.Result
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.AdminPanelController
import com.neurobs.js.dom.ElementFactory.{makeSelectBox, makeTextInput}
import com.neurobs.js.dom.{AnchorLink, ElementFactory, InputTextBox, RecordTableView, SelectSelectBox}
import com.neurobs.js.util.StringUtilities.dateTimeString
import com.neurobs.js.view.{Gui, ValueSelectBox}
import com.neurobs.js.dom.ElementFactory._
import org.scalajs.dom.raw.HTMLElement
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.util.{JsonPrinter, JsonUtil}
import scalatags.JsDom.all._

import scala.concurrent.{Future, Promise}

object EventServerEventListController {
	import com.neurobs.ccabserver.administrator.controller.TableNavigationController._

	case class CustomData
	(
		eventType: Option[String] = None,
		source: Option[String] = None,
		processed: Option[Boolean] = None,
		hasError: Option[Boolean] = None,
		isRetry: Option[Boolean] = None
	)

	class EventSearchForm extends SearchForm[CustomData] {
		class YesNoBox {
			val select = makeSelectBox(width = 10)
			val value = ValueSelectBox.yesNo(SelectSelectBox(select))
		}

		private val typeBox = InputTextBox(makeTextInput(width = 20))
		private val sourceBox = InputTextBox(makeTextInput(width = 20))
		private val processedBox = new YesNoBox
		private val hasErrorBox = new YesNoBox
		private val isRetryBox = new YesNoBox

		override def container: HTMLElement = {
			val temp = ElementFactory.makeFormTable(Array(
				("Type:", typeBox.text),
				("Source:", sourceBox.text),
				("Processed:", processedBox.select),
				("Is Retry:", isRetryBox.select),
				("Has Error:", hasErrorBox.select),
			))
			temp.style.margin = "0"
			temp
		}

		override def set(s: CustomData): Unit = {
			typeBox.setValue(s.eventType.getOrElse(""))
			sourceBox.setValue(s.source.getOrElse(""))
			processedBox.value.setSelectedValue(s.processed)
			hasErrorBox.value.setSelectedValue(s.hasError)
			isRetryBox.value.setSelectedValue(s.isRetry)
		}

		override def get: Result[CustomData] = Right(CustomData(
			eventType = typeBox.optionValue(),
			source = sourceBox.optionValue(),
			processed = processedBox.value.value,
			hasError = hasErrorBox.value.value,
			isRetry = isRetryBox.value.value
		))

		override def clear(): Unit = {
			typeBox.clear()
			sourceBox.clear()
			processedBox.value.setSelectedValue(None)
			hasErrorBox.value.setSelectedValue(None)
			isRetryBox.value.setSelectedValue(None)
		}
	}
	class EventAdapter(gui: Gui) extends Adapter[CustomData, EventServerEventData] {
		override def getId(d: EventServerEventData): Long = d.id

		override def get(search: NavigationSearchData[CustomData]): Future[Result[Seq[EventServerEventData]]] = {
			val temp = EventServerEventSearchData(
				eventType = search.data.eventType,
				source = search.data.source,
				processed = search.data.processed,
				hasError = search.data.hasError,
				isRetry = search.data.isRetry,
				minTime = search.minTime,
				maxTime = search.maxTime,
				maxId = search.maxId,
				limit = search.limit
			)
			val p = Promise[Result[Seq[EventServerEventData]]]
			CCABServer.searchEventServerEvents(temp) {
				case Left(error) => p.success(Left(error.message))
				case Right(data) => p.success(Right(data))
			}
			p.future
		}

		override def columnHeaders: Seq[RecordTableView.ColumnHeader] = { Seq(
			"DID",
			"Time",
			"Event Time",
			"Rec Time",
			"Type",
			"Source",
			"",
			"Processed",
			"Error",
			"Retry"
		)}

		override def recordRow(d: EventServerEventData): Seq[RecordTableView.CellContent] = {
			val contentLink = AnchorLink(makeLink(text = "Content"))
			contentLink.setHandler { () =>
				gui.showModalFormattedText(width = 50, title = s"Event ${d.id} Content", d.content)
			}

			val errorElement = d.errorId match {
				case None => span().render
				case Some(errorId) =>
					val errorLink = AnchorLink(makeLink(text = errorId.toString))
					errorLink setHandler { () =>
						val contentBox = div().render
						CCABServer.getError(errorId) { res =>
							res.map(_.data.map(_.map { edata =>
								gui.showModalFormattedText(width = 50, title = s"Error $errorId", edata.content)
							}))
						}
					}
					errorLink.anchor
			}
			Seq(
				d.id.toString,
				dateTimeString(d.createdAt),
				d.eventTime.map(dateTimeString),
				dateTimeString(d.receivedTime),
				d.eventType,
				d.eventSource,
				contentLink.anchor,
				d.processed.map(dateTimeString),
				errorElement,
				d.retryId.map(_.toString)
			)
		}

		override def createSearchForm: SearchForm[CustomData] = new EventSearchForm
	}

	def apply(gui: Gui): AdminPanelController = {
		val adapter = new EventAdapter(gui)
		new TableNavigationPanelController(title = "Event Server Events", adapter)
	}
}
